import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MessageService } from 'primeng/api';
import { AdminService } from '../../../service/admin.service';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';

@Component({
  selector: 'app-view-user-details',
  standalone: true,
  imports: [CommonModule, LucideIconsModule, PrimengImports],
  templateUrl: './view-user-details.component.html',
  styleUrl: './view-user-details.component.css',
  providers: [MessageService]
})
export class ViewUserDetailsComponent implements OnInit, OnChanges {
  @Input() userId: string | null = null;
  userDetails: any;
  isLoading: boolean = false;
  error: any = null;

  constructor(private adminService: AdminService, private messageService: MessageService) { }

  ngOnInit(): void {
    // No need to call fetchUserDetails here, ngOnChanges will handle it
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && changes['userId'].currentValue) {
      this.fetchUserDetails();
    }
  }

  fetchUserDetails(): void {
    if (!this.userId) {
      this.error = 'User ID is missing.';
      this.messageService.add({ severity: 'error', summary: 'Error', detail: this.error });
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.adminService.getUserDetailsForAdmin(this.userId).subscribe({
      next: (response) => {
        if (response && response.success) {
          // Ensure we always have a predictable object shape for template bindings
          this.userDetails = response.data || {};
          if (!this.userDetails.user) this.userDetails.user = {};
          if (!this.userDetails.wallet) this.userDetails.wallet = { balance: 0, pendingWithdrawals: 0, spendableBalance: 0 };
          if (!this.userDetails.investments) this.userDetails.investments = [];
          if (!this.userDetails.transactions) this.userDetails.transactions = [];
          if (!this.userDetails.withdrawals) this.userDetails.withdrawals = [];
        } else {
          this.error = (response && response.message) || 'Failed to load user details.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail: this.error });
          // keep userDetails as an empty object to avoid template crashes
          this.userDetails = { user: {}, wallet: { balance: 0, pendingWithdrawals: 0, spendableBalance: 0 }, investments: [], transactions: [], withdrawals: [] };
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.error = err.message || 'Error fetching user details.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.error });
        this.isLoading = false;
      }
    });
  }

  formatCurrency(amount: any): string {
    if (amount == null || isNaN(amount)) {
      return '0';
    }
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'initiated':
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'clock';
      case 'approved':
      case 'completed':
        return 'check-circle';
      case 'rejected':
      case 'canceled':
        return 'x-circle';
      case 'initiated':
      case 'processing':
        return 'loader-circle'; // Or another appropriate icon
      default:
        return 'info';
    }
  }
}